/**
 * Types for the careOS prototype surface being reconciled into this repo:
 * the patient profile and the labs & documents workflow. Kept separate from
 * the production models (`user.ts`, `meeting.ts`, …) because these are
 * prototype/demo shapes — they back the mock layer and the prototype state,
 * not real backend contracts. Production will map them onto the real models.
 *
 * Scope note: the prototype's homepage types (`Appointment`, `TodoItem`,
 * `AppView`) are intentionally NOT here — the careOS homepage is the repo's
 * `ProviderHome` (see `src/models/providerHome.ts`: `ProviderVisit`,
 * `ProviderTask`), and navigation is React Router, not an `AppView` enum.
 * The patient roster's `Patient` type is also deferred until the roster's
 * fate is decided (it may be superseded by ProviderHome's navigation).
 */

// ─── Prototype journey state ────────────────────────────────────────────────
// Drives the prototype bar's Stage dropdown, which steps a demo through the
// labs & documents scenarios a stakeholder needs to see. Each value is a
// *scenario* — a shape of the page you can't otherwise reach in a demo — not a
// step in the UI's own navigation:
//
//   'profile'  Steady state. No active order; the results gallery as-is.
//   'alert'    The "Lab Order Needed" homepage task has routed the provider
//              here (PRD, Entry Points §2). Banner above the tab header.
//   'review'   An active order has results back from the lab awaiting the
//              provider's read: PRD Pending state with ≥1 attachment, so the
//              Partial Complete chip shows and Mark as Reviewed is enabled.
//   'done'     That review happened — the order has left Active Orders and its
//              result is the newest tile in the gallery.
//
// Note there is no 'form' stage. Opening the new-order form is a button on the
// tab header, not a demo state, so it's local UI state inside `LabsTab`. As a
// stage it was indistinguishable from 'review' (both rendered the same inline
// order form) and made the dropdown misreport what was on screen.
export type JourneyStage = 'alert' | 'profile' | 'review' | 'done';

// ─── Patient summary detail (Patient Summary drawer) ────────────────────────
// What the drawer behind the steady-state Patient Summary card shows: what's
// new since the member's last visits, what's working and what isn't, how the
// member sounds lately, and the engagement plan set at each visit.

/** One line of an engagement plan: its area ("Nutrition") and the plan. */
export interface SummaryPlanItem {
  label: string;
  text: string;
}

export interface SummaryMeeting {
  id: string;
  /** Pinned literal ("Feb 23, 2026"), never derived. */
  date: string;
  visitType: string;
  provider: string;
  /** Coach plans read as numbered goals; provider plans as grouped
   *  recommendations. Each is shown the way its author writes it. */
  planStyle: 'goals' | 'recommendations';
  plan: SummaryPlanItem[];
}

export interface PatientSummaryDetail {
  highlights: string[];
  progress: { working: string[]; challenging: string[] };
  /** The member's own words, where they came from, and a one-line read. */
  sentiment: { quote: string; source: string; date: string; read: string };
  /** Newest first; only the first opens by default. */
  meetings: SummaryMeeting[];
}

// ─── App onboarding status (Gut Checks card) ────────────────────────────────
// Where the patient is in getting started with the mobile app. Progresses
// never-logged-in → onboarding → baseline-complete. There's no trackable
// "downloaded but not logged in" signal, so that state was collapsed into
// never-logged-in. Once a patient reaches baseline-complete the backend sets
// it permanently (it never regresses, e.g. across reinstalls), so the check
// mark persists in perpetuity.
export type AppStatus = 'never-logged-in' | 'onboarding' | 'baseline-complete';

export const APP_STATUS_ORDER: AppStatus[] = [
  'never-logged-in',
  'onboarding',
  'baseline-complete',
];

// Short label + longer description for each state. `label` shows in the card
// pill and the prototype bar's App Status dropdown; `description` is the pill's
// tooltip/aria.
export const APP_STATUS_META: Record<
  AppStatus,
  { label: string; description: string }
> = {
  'never-logged-in': {
    label: 'Never logged in',
    description: 'User has not downloaded or created an account',
  },
  onboarding: {
    label: 'Onboarding',
    description: 'User has created an account and is setting up their profile',
  },
  'baseline-complete': {
    label: 'Baseline complete',
    description: 'User has completed onboarding and submitted their first gut check',
  },
};

// ─── Labs & documents workflow ──────────────────────────────────────────────

export type LabOrderStatus =
  | 'submitted'
  | 'pending-collection'
  | 'processing'
  | 'pending-review' // results PDF attached to the order, awaiting clinical sign-off
  | 'completed'
  | 'out-of-range';

// How a result entered careOS.
export type LabDeliveryMethod = 'auto-received' | 'uploaded' | 'in-flight';

export interface OrderAttachment {
  id: string;
  fileName: string;
  // Source + range are OPTIONAL — a manually uploaded result may land
  // untagged. Providers designate or change these per-attachment from the
  // preview drawer. "Mark as Read" stays disabled until every attachment has
  // a `range` set.
  source?: 'patient-copy' | 'provider-sent';
  range?: 'in-range' | 'out-of-range';
  uploadedAt: string; // "Apr 22, 2026" style — display only
  uploadedTimestamp?: number; // Unix ms — used for time-vs-date badge logic
  // Blob URL produced via `URL.createObjectURL(file)` at upload time. Valid for
  // the lifetime of the document; we don't revoke in the prototype since data
  // doesn't survive a page reload anyway.
  fileUrl?: string;
}

export interface ActiveLabOrder {
  patientId: string;
  patientName: string;
  tests: string[];
  vendor: string;
  orderedDate: string;
  status: LabOrderStatus;
  // Result PDFs attached to this active order, in upload order (oldest first).
  // The card stays in the Active Orders section until "Mark as Read" transitions
  // status to a terminal value (completed / out-of-range), at which point the
  // attachments' aggregate metadata informs the Results-grid card's vendor and
  // delivery-badge presentation.
  attachments?: OrderAttachment[];
}

export interface LabEntry {
  id: string;
  date: string;
  status: LabOrderStatus;
  tests: string[];
  vendor: string;
  deliveryMethod: LabDeliveryMethod; // how the result entered careOS
  fileName?: string; // original filename for uploaded docs only
  // Result files added after the entry landed ("Add result" / drag-and-drop in
  // the preview drawer). When present this is the entry's FULL file list —
  // the original `fileName` file is seeded in as the first item on first add.
  attachments?: OrderAttachment[];
}

// ─── Follow-ups (FollowUpsCard) ─────────────────────────────────────────────
// NB: distinct from `providerHome.ts`'s `VisitStatus` — that's a visit
// lifecycle (occurred / no_show / …); this is a follow-up scheduling state.
// Different domain, casing, and values; intentionally not merged.
export type FollowUpStatus = 'Confirmed' | 'Unconfirmed' | 'Unscheduled';

export interface FollowUp {
  provider: string;
  status: FollowUpStatus;
  date: string;
  time?: string | null;
}

// ─── Claims Insights ────────────────────────────────────────────────────────
// Pre-initial-visit read on a member, computed from claims data (Claims
// Insights PRD). Not a tab of its own: these insights render INSIDE the
// Patient Summary tab, before the first visit only. In `before-first-visit`
// they're all the tab shows — there's no prior care to synthesize. In
// `summary-ready` (still before the first visit) the onboarding chief
// complaint leads and the claims insights stay beneath it unchanged. After
// the first visit (`steady-state`) the tab shows the transcript chief
// complaint alone. `ClaimsInsightsState` is a
// prototype-only demo axis (see PrototypeBar) standing in for the real-world
// drivers: whether the member has onboarded and completed their first visit,
// and whether their client's claims feed has data at all.
export type ClaimsInsightsState = 'before-first-visit' | 'summary-ready' | 'steady-state' | 'no-claims-data';

// Both insight dimensions share the same three-tier framing (PRD "Semantic
// states"). Severity maps onto the design system's RiskDots indicator:
// low = 1 dot (scrubs), moderate = 2 (orange), high = 3 (fuschia).
export type InsightSeverity = 'low' | 'moderate' | 'high';

export type InsightType = 'medication-management' | 'clinical-volatility';

export interface ClaimsInsight {
  id: string;
  type: InsightType;
  severity: InsightSeverity;
  // One clinician-facing sentence, standardized per insight type/severity
  // (PRD semantic-states table). No headline — the row is dots + sentence.
  copy: string;
}

export interface ClaimsInsightsData {
  hasClaimsData: boolean;
  insights: ClaimsInsight[];
}

// ─── Pharmacy (PharmacyTab) ──────────────────────────────────────────────────
// The PharmD surface: what the member is currently taking, what they've come
// off, and where the care team suspects they aren't taking something reliably.
// Deliberately thin — this prototype covers display / add / archive only.

export type MedicationKind = 'medication' | 'supplement';

// 'archived' is the provider-facing "no longer taking" state. Kept rather than
// deleted so the care team retains the history of what was tried.
export type MedicationStatus = 'active' | 'archived';

export interface MedicationPrescriber {
  name: string;
  specialty: string;
}

export interface MedicationPharmacy {
  name: string;
  address: string;
}

export interface MedicationRefills {
  remaining: number;
  total: number;
  nextDue?: string; // "Sep 14, 2026" display style
}

export interface Medication {
  id: string;
  kind: MedicationKind;
  name: string;
  // Free text — "20 mg daily", "2 capsules AM". Optional because a med list
  // imported from an outside source (see the Healthie list this view replaces)
  // often arrives as bare names with no dose attached.
  dose?: string;
  status: MedicationStatus;
  // "Jan 7, 2026" display style, matching LabEntry.date. Labeled "Prescribed"
  // for medications and "Added" for supplements in the tab.
  addedDate: string;
  archivedDate?: string;
  // True when this entry arrived via e-prescribing rather than being typed in
  // manually through the Add Medication form.
  ePrescribed?: boolean;
  instructions?: string; // patient-facing, shown alongside the dosage
  notes?: string; // internal clinician note — not shown to the patient
  prescriber?: MedicationPrescriber;
  pharmacy?: MedicationPharmacy;
  refills?: MedicationRefills;
}
